import type {ReactNode} from 'react'
import {Heading, Image, Link, River, Text, Stack} from '@primer/react-brand'
import type {RiverProps} from '@primer/react-brand'

import {getImageSources} from '@github-ui/swp-core/lib/utils/images'

import {AssetTypeEnum} from '../types'
import type {Brand, LabelAssetLinkItem} from '../types'
import {AssetVideo} from './Video'
import {getBrandDitherColor, renderWithNoMarks, useDitherBackground} from '../utils'
import styles from './FlexSuiteRiverStack.module.css'

export type FeaturedStackProps = {
  items?: LabelAssetLinkItem[]
  alignment?: 'alternate' | 'start' | 'end'
  variant?: RiverProps['variant']
  brand?: Brand
  showDither?: boolean
}

const RIVER_ALIGNMENTS: Array<RiverProps['align']> = ['start', 'end']

type DitherContainerRef = ReturnType<typeof useDitherBackground>['containerRef']

type RiverStackItemProps = {
  item: LabelAssetLinkItem
  align: RiverProps['align']
  variant: RiverProps['variant']
  brand?: Brand
  showDither?: boolean
}

function RiverItemMedia({item}: {item: LabelAssetLinkItem}) {
  const {assetType, optionImage, optionVideo, optionPoster, autoplay, videoSource} = item.fields

  return (
    <>
      {assetType === AssetTypeEnum.enum.Image && optionImage && (
        <Image
          as="picture"
          src={`${optionImage.fields.file.url}?fm=webp`}
          sources={getImageSources(optionImage.fields.file.url, {maxWidth: 720})}
          alt={optionImage.fields.description ?? ''}
        />
      )}
      <AssetVideo
        assetType={assetType}
        optionVideo={optionVideo}
        optionPoster={optionPoster}
        autoplay={autoplay}
        videoSource={videoSource}
      />
    </>
  )
}

function RiverStackItemContent({
  item,
  align,
  variant,
  containerRef,
  ditherBackground,
}: Omit<RiverStackItemProps, 'brand' | 'showDither'> & {
  containerRef?: DitherContainerRef
  ditherBackground?: ReactNode
}) {
  const {heading, description, href, label, alt, position} = item.fields

  return (
    <River align={align} variant={variant}>
      <River.Visual ref={containerRef} fillMedia={false} imageBackgroundColor="subtle" className={styles.visual}>
        <div className={styles.mediaFrame} data-image-position={position}>
          {ditherBackground}
          <RiverItemMedia item={item} />
        </div>
      </River.Visual>
      <River.Content>
        <Heading as="h3" size="5">
          {renderWithNoMarks(heading)}
        </Heading>
        <Text>{renderWithNoMarks(description)}</Text>
        <Link href={href} {...(alt ? {'aria-label': alt} : {})}>
          {label}
        </Link>
      </River.Content>
    </River>
  )
}

function RiverStackItem({showDither = false, brand, ...props}: RiverStackItemProps) {
  const {containerRef, ditherBackground} = useDitherBackground({
    className: styles.dither,
    enabled: showDither,
    params: {color: getBrandDitherColor(brand), type: 'random', fadeIn: true},
  })

  return <RiverStackItemContent {...props} containerRef={containerRef} ditherBackground={ditherBackground} />
}

export const FlexSuiteRiverStack = ({
  items,
  alignment = 'start',
  variant = 'gridline',
  brand,
  showDither = false,
}: FeaturedStackProps) => {
  if (!items || items.length === 0) return null

  return (
    <Stack direction="vertical" padding="none" gap="spacious">
      {items.map((item, index) => {
        const align: RiverProps['align'] =
          alignment === 'alternate' ? RIVER_ALIGNMENTS[index % RIVER_ALIGNMENTS.length] : alignment

        return (
          <RiverStackItem
            key={item.sys.id}
            item={item}
            align={align}
            variant={variant}
            brand={brand}
            showDither={showDither}
          />
        )
      })}
    </Stack>
  )
}
